import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import '../../fonts/fonts.css'
import { cartRemove, cartUpdate } from '../App/actions'
import { getAgencyLegalId, saveAgencyLegalId } from '../../utils/legalId'
import shoppingBagIcon from '../../images/icon-shopping-bag.png'
import trashIcon       from '../../images/icon-trash.png'
import logoTgdEval     from '../../images/logo-tgd-eval.png'
import iconConduite    from '../../images/icon-conduite.png'
import iconHardware    from '../../images/icon-hardware.png'
import PageHeader from '../../components/PageHeader'
import {
  PageWrapper,
  StepsBar, StepItem, StepNumber, StepDivider,
  CartGrid, ItemsList,
  ItemCard, ItemThumb, ItemInfo, ItemCategory, ItemTitle,
  QuantityControls, QtyButton, QtyValue,
  ItemPrice, RemoveButton,
  SummaryCardWrapper, SummaryCard, SummaryTitle, Divider,
  SummaryRow, SummaryLabel, SummaryValue,
  SubtotalRow, SubtotalLabel, SubtotalValue,
  TotalRow, TotalLabel, TotalValue,
  CheckoutButton, TrustText,
  StateContainer, EmptyIllustration, StateTitle, StateSubtitle, PrimaryButton,
  PaymentSection, PaymentTitle, PayNowBtn, LegalText, StripeError,
  RetractOverlay, RetractModal, RetractTitle, RetractBody, RetractBtnRow,
  RetractCancelBtn, RetractAcceptBtn,
  StripeSecureBadge, PayConfirmInfo,
} from './styledComponents'


const apiBase = process.env.REACT_APP_TGDAPI_SERVER || ''

const CATEGORY_COLORS = {
  Eval: '#6462FC', Conduite: '#3494FA', Automatique: '#31D0BE',
  Eco: '#00C52B', Recyclage: '#7C4DFF', Code: '#3494FA', Hardware: '#00C8F8',
}
const CATEGORY_ICON_IMGS = { Eval: logoTgdEval, Conduite: iconConduite, Hardware: iconHardware }
const CATEGORY_ICONS     = { Automatique: '⚙️', Eco: '🌱', Recyclage: '🔄', Code: '📖' }
const TRUST_TEXT         = `Paiement 100% sécurisé\nAccès immédiat après commande\nSupport disponible`
const fmt                = (n) => n.toFixed(2).replace('.', ',') + ' €'

const PanierHeader = () => (
  <PageHeader icon={shoppingBagIcon} title='Mon panier' subtitle='Repartez tout·e équipé·e' />
)

const Stepper = ({ step }) => (
  <StepsBar>
    <StepItem active={step === 0} completed={step > 0}>
      <StepNumber active={step === 0} completed={step > 0}>1</StepNumber>
      Votre panier
    </StepItem>
    <StepDivider />
    <StepItem active={step === 1}>
      <StepNumber active={step === 1}>2</StepNumber>
      Paiement
    </StepItem>
  </StepsBar>
)

const SummaryPanel = ({ items, priceHT, priceTVA, totalPrice, withCheckout, onCheckout }) => (
  <SummaryCardWrapper>
    <SummaryCard>
      <SummaryTitle>Récapitulatif</SummaryTitle>
      <Divider />

      {items.map(({ formation, quantity }) => (
        <SummaryRow key={formation.id}>
          <SummaryLabel title={formation.title}>{formation.title} x {quantity}</SummaryLabel>
          <SummaryValue>{fmt(formation.price * quantity)}</SummaryValue>
        </SummaryRow>
      ))}

      <Divider />

      <SubtotalRow>
        <SubtotalLabel>Sous-total HT</SubtotalLabel>
        <SubtotalValue>{fmt(priceHT)}</SubtotalValue>
      </SubtotalRow>
      <SubtotalRow>
        <SubtotalLabel>TVA 20%</SubtotalLabel>
        <SubtotalValue>{fmt(priceTVA)}</SubtotalValue>
      </SubtotalRow>

      <Divider />

      <TotalRow>
        <TotalLabel>TOTAL TTC</TotalLabel>
        <TotalValue>{fmt(totalPrice)}</TotalValue>
      </TotalRow>

      {withCheckout && (
        <CheckoutButton onClick={onCheckout}>Valider le panier</CheckoutButton>
      )}

      <TrustText>{TRUST_TEXT}</TrustText>
    </SummaryCard>
  </SummaryCardWrapper>
)

const Cart = () => {
  const history    = useHistory()
  const dispatch   = useDispatch()
  const items      = useSelector(state => state.toJS().global.cartItems || [])
  const totalPrice = items.reduce((sum, { formation, quantity }) => sum + formation.price * quantity, 0)
  const removeFromCart = (id)       => dispatch(cartRemove(id))
  const updateQuantity = (id, qty)  => dispatch(cartUpdate(id, qty))

  const agencyToken       = useSelector(state => state.toJS().global.agencytoken)       || ''
  const agencyName        = useSelector(state => state.toJS().global.agencyName)         || ''
  const notificationEmail = useSelector(state => state.toJS().global.notificationEmail) || ''
  const loginStatus       = useSelector(state => state.toJS().global.loginStatus)       || ''
  const authReady         = loginStatus === 'connected'
  const [step, setStep]               = useState(0)     // 0=cart  1=payment confirm
  const [loading, setLoading]         = useState(false)  // API call in progress
  const [showRetract, setShowRetract] = useState(false)
  const [legalIdModalOpen, setLegalIdModalOpen] = useState(false)
  const [legalIdInput, setLegalIdInput] = useState('')
  const [legalIdError, setLegalIdError] = useState('')
  const [legalIdLoading, setLegalIdLoading] = useState(false)
  const [sessionErr, setSessionErr]   = useState('')

  const priceHT  = totalPrice / 1.2
  const priceTVA = totalPrice - priceHT

  const beginCheckoutFlow = async () => {
    if (!authReady || !agencyName || !agencyToken) {
      setSessionErr('Vous devez être connecté pour finaliser votre achat.')
      return
    }

    setLegalIdError('')
    setLegalIdInput('')
    setLegalIdLoading(true)

    try {
      const legalId = await getAgencyLegalId(agencyName, agencyToken)
      if (legalId && legalId.hasLegalId) {
        setLegalIdModalOpen(false)
        setShowRetract(true)
        return
      }

      setLegalIdModalOpen(true)
    } catch (e) {
      setLegalIdError(e.message || 'Impossible de vérifier le SIREN/SIRET de votre agence.')
      setLegalIdModalOpen(true)
    } finally {
      setLegalIdLoading(false)
    }
  }

  const submitLegalId = async () => {
    const sanitized = legalIdInput.replace(/\D/g, '')

    if (sanitized.length !== 9 && sanitized.length !== 14) {
      setLegalIdError('Le numéro doit contenir 9 chiffres (SIREN) ou 14 chiffres (SIRET).')
      return
    }

    setLegalIdError('')
    setLegalIdLoading(true)

    try {
      await saveAgencyLegalId(agencyName, sanitized, agencyToken)
      setLegalIdModalOpen(false)
      setShowRetract(true)
    } catch (e) {
      setLegalIdError(e.message || 'Impossible d’enregistrer votre SIREN/SIRET.')
    } finally {
      setLegalIdLoading(false)
    }
  }

  const handleCheckout = async () => {
    setShowRetract(false)
    setLoading(true)
    setSessionErr('')

    const base = process.env.REACT_APP_SERVER_CLIENT || window.location.origin
    // Stripe remplace {CHECKOUT_SESSION_ID} : CartSuccess suit exactement la commande payée
    const successUrl = `${base}/cart/success?session_id={CHECKOUT_SESSION_ID}`
    const cancelUrl  = `${base}/cart/cancel`

    try {
      const resp = await fetch(`${apiBase}/stripe/checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${agencyToken}`,
        },
        body: JSON.stringify({
          items: items.map(({ formation, quantity }) => ({
            productId:  formation.id,
            trainingId:  formation.trainingId,
            sku:        formation.sku || '',
            name:       formation.title,
            unitAmount: Math.round(formation.price * 100),
            quantity,
            currency:   'eur',
          })),
          successUrl,
          cancelUrl,
          ...(notificationEmail ? { customerEmail: notificationEmail } : {}),
          metadata: { source: 'tgd-admin' },
        }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}))
        throw new Error(err.message || `Erreur ${resp.status}`)
      }

      const data        = await resp.json()
      const redirectUrl = data.checkoutUrl || data.url
      if (!redirectUrl) throw new Error('URL de paiement manquante dans la réponse')
      window.location.href = redirectUrl

    } catch (e) {
      setLoading(false)
      setSessionErr(e.message || 'Une erreur est survenue lors de la préparation du paiement.')
    }
  }

  /* Panier vide */

  if (items.length === 0 && step === 0) {
    return (
      <PageWrapper>
        <PanierHeader />
        <Stepper step={0} />
        <StateContainer>
          <EmptyIllustration>🛒</EmptyIllustration>
          <StateTitle>Votre panier est vide !</StateTitle>
          <StateSubtitle>Parcourez le magasin et ajoutez des formations pour commencer.</StateSubtitle>
          <PrimaryButton onClick={() => history.push('/store')}>
            Découvrir les formations →
          </PrimaryButton>
        </StateContainer>
      </PageWrapper>
    )
  }

  /* Step 0 — Panier */

  if (step === 0) {
    return (
      <PageWrapper>
        <PanierHeader />
        <Stepper step={0} />
        <CartGrid>
          <ItemsList>
            {items.map(({ formation, quantity }) => {
              const color     = formation.color || CATEGORY_COLORS[formation.category] || '#6462FC'
              const iconImg   = formation.iconUrl || CATEGORY_ICON_IMGS[formation.category]
              const iconEmoji = CATEGORY_ICONS[formation.category] || '🎓'
              return (
                <ItemCard key={formation.id}>
                  <ItemThumb color={color}>
                    {iconImg
                      ? <img src={iconImg} alt='' />
                      : <span style={{ fontSize: '1.4rem' }}>{iconEmoji}</span>
                    }
                  </ItemThumb>
                  <ItemInfo>
                    <ItemCategory>{formation.category.toUpperCase()}</ItemCategory>
                    <ItemTitle title={formation.title}>{formation.title}</ItemTitle>
                  </ItemInfo>
                  <QuantityControls>
                    <QtyButton onClick={() => updateQuantity(formation.id, quantity - 1)}>−</QtyButton>
                    <QtyValue>{quantity}</QtyValue>
                    <QtyButton onClick={() => updateQuantity(formation.id, quantity + 1)}>+</QtyButton>
                  </QuantityControls>
                  <ItemPrice>{fmt(formation.price * quantity)}</ItemPrice>
                  <RemoveButton onClick={() => removeFromCart(formation.id)} title='Supprimer'>
                    <img src={trashIcon} alt='Supprimer' />
                  </RemoveButton>
                </ItemCard>
              )
            })}
          </ItemsList>

          <SummaryPanel
            items={items}
            priceHT={priceHT}
            priceTVA={priceTVA}
            totalPrice={totalPrice}
            withCheckout
            onCheckout={() => { setSessionErr(''); setStep(1) }}
          />
        </CartGrid>
      </PageWrapper>
    )
  }

  /* Step 1 — Confirmation → redirection Stripe */

  return (
    <PageWrapper>
      <PanierHeader />
      <Stepper step={1} />
      <CartGrid>

        <PaymentSection>
          <PaymentTitle>Informations de paiement</PaymentTitle>

          <StripeSecureBadge>
            🔒 Paiement sécurisé par Stripe
          </StripeSecureBadge>

          <PayConfirmInfo>
            Vous allez être redirigé vers la page de paiement sécurisée Stripe pour finaliser
            votre commande. Vos données bancaires ne transitent jamais par nos serveurs.
          </PayConfirmInfo>

          {sessionErr && <StripeError>{sessionErr}</StripeError>}

          <PayNowBtn
            disabled={loading || !authReady || legalIdLoading}
            onClick={() => !loading && authReady && beginCheckoutFlow()}
          >
            {loading ? 'Préparation du paiement…' : legalIdLoading ? 'Vérification…' : !authReady ? 'Chargement…' : 'Payer maintenant'}
          </PayNowBtn>

          <LegalText>
            En cliquant sur « Payer maintenant », vous serez redirigé vers Stripe pour saisir
            vos informations bancaires en toute sécurité. Vous acceptez nos conditions générales
            de vente et confirmez avoir pris connaissance de votre droit de rétractation de 14 jours.
          </LegalText>
        </PaymentSection>

        <SummaryPanel
          items={items}
          priceHT={priceHT}
          priceTVA={priceTVA}
          totalPrice={totalPrice}
          withCheckout={false}
        />
      </CartGrid>

      {legalIdModalOpen && (
        <RetractOverlay onClick={() => setLegalIdModalOpen(false)}>
          <RetractModal onClick={e => e.stopPropagation()}>
            <RetractTitle>Votre SIREN ou SIRET</RetractTitle>
            <RetractBody>
              Afin de poursuivre votre achat, veuillez renseigner le SIREN ou le SIRET de votre établissement.
            </RetractBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.25rem' }}>
              <input
                type='text'
                value={legalIdInput}
                onChange={(e) => setLegalIdInput(e.target.value)}
                placeholder='SIREN ou SIRET'
                style={{
                  width: '100%',
                  height: '2.75rem',
                  border: '1px solid #D9E1F2',
                  borderRadius: '0.5rem',
                  padding: '0 0.875rem',
                  fontFamily: 'Poppins',
                  fontSize: '0.95rem',
                  color: '#111827',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {legalIdError && (
                <div style={{ fontFamily: 'Poppins', fontSize: '0.8rem', color: '#E11D48', marginTop: '-0.2rem' }}>
                  {legalIdError}
                </div>
              )}
            </div>
            <RetractBtnRow>
              <RetractCancelBtn onClick={() => { setLegalIdModalOpen(false); setLegalIdError('') }}>Annuler</RetractCancelBtn>
              <RetractAcceptBtn onClick={submitLegalId} disabled={legalIdLoading}>
                {legalIdLoading ? 'Validation…' : 'Valider'}
              </RetractAcceptBtn>
            </RetractBtnRow>
          </RetractModal>
        </RetractOverlay>
      )}

      {showRetract && (
        <RetractOverlay onClick={() => setShowRetract(false)}>
          <RetractModal onClick={e => e.stopPropagation()}>
            <RetractTitle>Informations sur le droit de rétractation</RetractTitle>
            <RetractBody>
              En sélectionnant « J'accepte », vous donnez votre consentement à la livraison
              immédiate ou l'accès au contenu numérique ou à l'abonnement que vous achetez.
              Ce faisant, vous perdrez votre droit de résiliation de 14 jours accordé par la
              loi, dès que la livraison ou l'accès se produit. Cela n'affecte aucun droit
              contractuel exprès que vous pourriez autrement avoir.
            </RetractBody>
            <RetractBtnRow>
              <RetractCancelBtn onClick={() => setShowRetract(false)}>Annuler</RetractCancelBtn>
              <RetractAcceptBtn onClick={handleCheckout} disabled={loading}>
                J'accepte
              </RetractAcceptBtn>
            </RetractBtnRow>
          </RetractModal>
        </RetractOverlay>
      )}
    </PageWrapper>
  )
}

export default Cart
